/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Reservation} from "types/entities/Reservation";
import {PercentBadgeIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import BookingPackageUpsellsHeader from "pages/Front/Booking/components/PackageUpsell/BookingPackageUpsellsHeader";
import PackageUpsellItem from "pages/Front/Booking/components/PackageUpsell/PackageUpsellItem";
import {packageItemUpsellPersonType, sortPackageItemsByPosition} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {toast} from "react-toastify";
import {closeModal} from "helpers/actions";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import BookingAction from "store/booking/BookingAction";
import {DEFAULT_HALL_ID} from "helpers/config";
import {PackagesCart} from "types/entities/PackagesCart";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackageItem} from "types/entities/PackageItem";
import {getSelectedUpsellPackages} from "utils/packageUtils";
import ModalScrollableClose from "components/ModalScrollableClose";
import SellerAction from "store/seller/SellerAction";
import {useGetGroupedUpsells} from "utils/upsellsUtils";
import GroupedPackageUpsellItem from "pages/Front/Booking/components/PackageUpsell/GroupedPackageUpsellItem";
import CombinationOfVariantPackageUpsellItem
    from "pages/Front/Booking/components/PackageUpsell/CombinationOfVariantPackageUpsellItem";

interface BookingSellerUpsellsModalProps {
    reservation: Reservation;
}

const BookingSellerUpsellsModal = (props: BookingSellerUpsellsModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const packageItemsWithoutGroupOptions = useTypedSelector(state => state.bookingState.bookingProcess?.availablePackageItems?.upsells);
    const persons = props.reservation.persons;
    const seller_id = useTypedSelector(state => state.sessionState.session?.uid) || 1;
    const packagesCart = props.reservation.packagesCart;
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const selected_timeslot_id = props.reservation.reservationSlots[0].timeslot_id;
    const [packageItemsList, setPackageItemsList] = useState<PackageItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const submitPackageUpsells = (): void => {
        if (!props.reservation.packagesCart) {
            toast.error("No packageCart found");
            return;
        }

        const selectedUpsellPackages = getSelectedUpsellPackages(selectedUpsells);
        if (selectedUpsellPackages.length === 0) {
            dispatch(closeModal());
            return;
        }

        setIsLoading(true);

        dispatch(SellerAction.setUpsellsToReservationBySeller(props.reservation.id, selectedUpsellPackages, seller_id))
            .then(r => {
                dispatch(BookingAction.clearUpsells());

                dispatch(closeModal());
                toast.success("Upsells have been added");
            })
            .catch(err => {
                setIsLoading(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getAvailableItems = () => {
        // console.log(packageItemsWithoutGroupOptions);
        const sortedPackageItemsList = packageItemsWithoutGroupOptions ? sortPackageItemsByPosition(packageItemsWithoutGroupOptions) : [];
        return sortedPackageItemsList.length > 0
            ? sortedPackageItemsList
                  .filter(packageItem => {
                      return packageItemUpsellPersonType(
                          {
                              adults: props.reservation.packagesCart?.drivers_count_adult,
                              children: props.reservation.packagesCart?.drivers_count_child,
                          },
                          packageItem.person_type
                      );
                  })
                  .filter(packageItem => {
                      if (packageItem.minimum_persons_count === 0) return true;
                      if (packageItem.minimum_persons_count > 0) {
                          return persons >= packageItem.minimum_persons_count;
                      }
                  })
                  .filter(packageItem => packageItem.is_active != 0)
            : [];
    };

    const fetchAvailablePackageItems = (packagesCart: PackagesCart) => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                {
                    adults: packagesCart.drivers_count_adult,
                    children: packagesCart.drivers_count_child,
                    date: packagesCart.date,
                    package_category_name: props.reservation.category as PackageCategory,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: [1],
                    timeslot_id: selected_timeslot_id,
                },
                seller_id
            )
        ).then(r => {});
    };

    const {groupedUpsellsAll} = useGetGroupedUpsells(packageItemsList);

    useEffect(() => {
        if (packagesCart) {
            fetchAvailablePackageItems(packagesCart);
        }
    }, [packagesCart]);

    useEffect(() => {
        const items = getAvailableItems();
        setPackageItemsList(items);
    }, [packageItemsWithoutGroupOptions]);

    return (
        <CardComponent
            className={
                "BookingSellerUpsellsModal w-full md:min-w-[700px] lg:min-w-[980px] max-w-90vw !p-0 border border-gray-200 relative overflow-hidden max-h-full flex flex-col h-auto"
            }
        >
            <div
                className={
                    "top-0 z-30 sticky flex flex-row justify-between rounded-tl-lg rounded-tr-lg w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-50 text-gray-900 drop-shadow-sm"
                }
            >
                <div className={"flex flex-row items-center"}>
                    <PercentBadgeIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{t("Upsells")}</h4>
                </div>
                <ModalScrollableClose />
            </div>
            <div className={"flex w-full flex-col px-3 pt-3 pb-4 bg-gray-50 overflow-hidden rounded-bl-lg rounded-br-lg h-auto"}>
                <div className={"flex flex-col overflow-y-auto h-full overscroll-contain scrollable-parent"}>
                    {packageItemsList.length > 0 && (
                        <>
                            <BookingPackageUpsellsHeader className={"!pt-0"} title={t("Please select your extras")} />
                            <div className={"space-y-4"}>
                                {groupedUpsellsAll.map((item, index) =>
                                    item.is_variant ? (
                                        item?.packageItemVariant?.minimum_count && item.packageItemVariant.minimum_count > 1 ? (
                                            <CombinationOfVariantPackageUpsellItem key={item.packageItemVariant.id} isSeller={true} item={item} />
                                        ) : (
                                            <GroupedPackageUpsellItem key={item.variant_id} isSeller={true} item={item} />
                                        )
                                    ) : (
                                        <PackageUpsellItem
                                            key={item.items[0].id}
                                            isSeller={true}
                                            isNewLayout={true}
                                            packageItem={item.items[0]}
                                            selectedPackage={null}
                                        />
                                    )
                                )}
                            </div>
                        </>
                    )}
                    {packageItemsList.length === 0 && <div className={"text-center"}>{t("No upsells found for selected package.")}</div>}
                    <div className={"flex flex-row mt-4 w-full text-white justify-center"}>
                        <ButtonSubmit
                            className={
                                "inline-flex items-center px-4 sm:px-3 py-3 w-auto rounded-md sm:min-w-[210px] !font-medium !text-base sm:!text-lg sm:!leading-none"
                            }
                            icon={"ticket"}
                            id="ButtonSubmitUpsells"
                            isLoading={isLoading}
                            label={selectedUpsells.length === 0 ? t("Continue without upsells") : t("Continue")}
                            onClick={submitPackageUpsells}
                        />
                    </div>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerUpsellsModal;
